/**
 * BdiGenoLab.jsx — iter650
 * Breast Disease Intelligence (BDI) — "🧬 Geno Lab" UI section.
 *
 * Renders 12 ranked disease cards (educational, NON-diagnostic). Each card
 * shows: Association Level pill, activation score, biological drivers,
 * activated pathways, modifiable factors, top contributing factors, and a
 * `Personalized Prevention Targets` block.
 *
 * Includes:
 *   - AI Enrichment button (top-3 → Claude → BG/EN/ES recommendations)
 *   - "Save snapshot" for 3-month trend tracking
 *   - 3-month trend chart for the top disease (Recharts)
 *
 * Strict lexicon — NEVER uses "cancer/risk/probability/diagnosis". Uses
 * "biological association · activated pathway · modifiable factor ·
 * personalized prevention opportunity".
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useBdiHighlight } from "@/utils/bdiHighlight";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Dna, Sparkles, Loader2, ShieldAlert, RefreshCw, History, Save,
  ChevronRight, ChevronDown, FlaskConical, BookOpen, Stethoscope,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import MethodologyInfo from "@/components/MethodologyInfo";  // iter701 — methodology popup
import BdiPrivacyUtilityBanner from "@/components/breast_intel/BdiPrivacyUtilityBanner";  // iter707d
import PathwaySensitivitySlider from "@/components/breast_intel/PathwaySensitivitySlider";  // iter707d
import DiscoveryCardPanel, { isDiscoveryEligible } from "@/components/breast_intel/DiscoveryCardPanel";  // iter849
import { getPathwayMetaphor, toneCls } from "@/utils/pathwayMetaphors";  // iter992

const TX = {
  bg: {
    title: "🧬 Geno Lab — Biological Disease Intelligence",
    subtitle:
      "Образователен профил на биологичните асоциации между Вашите BHI показатели и {n} гръдни заболявания. Изобразява АКТИВИРАНИ ПЪТИЩА и ФАКТОРИ, КОИТО МОЖЕ ДА ПРОМЕНИМ — не е диагноза, не предсказва развитие и не заменя лекар.",
    legalNote:
      "За образователни цели · pathway-базиран модел · НЕ е диагностично или прогностично средство. Винаги обсъждайте с Вашия лекар.",
    refresh: "Обнови",
    enrich: "✨ AI препоръки (топ 3)",
    enriching: "Генерира AI препоръки…",
    saveSnapshot: "Запиши snapshot ръчно",
    snapshotSaved: "Snapshot записан",
    autoSnapshotHint: "🔄 Snapshot-ите се записват автоматично при обновяване на лабораторни или lifestyle данни (макс. 1 на 6 часа).",
    trends: "3-месечни тенденции",
    cached: "от кеш",
    loading: "Зареждане…",
    empty: "Няма данни за изобразяване.",
    confidence: "Сигурност на профила",
    cohort: "Разпределение на {n}-те карти",
    high: "Силна",
    elevated: "Повишена",
    moderate: "Умерена",
    low: "Ниска",
    section_drivers: "Биологични двигатели",
    section_pathways: "Активирани сигнални пътища",
    section_modifiable: "Фактори, които може да променим",
    section_prevention: "Цели за персонализирана профилактика",
    section_contrib: "Топ принос към профила",
    factor_label: "фактор",
    weight: "тегло",
    burden: "натоварване",
    score_label: "активационен скор",
    seeMore: "Виж пълните данни",
    seeLess: "Скрий",
    expandAll: "Разгъни всички",
    collapseAll: "Сгъни всички",
    aiTitle: "🔬 AI Lab препоръки (само за DUTCH/ДНК)",
    aiHint:
      'Тесно-фокусирани препоръки, извлечени САМО от най-силно асоциираните 3 биологични карти по-горе (DUTCH хормонални метаболити и ДНК полиморфизми). За холистичен седмичен план използвайте „🤝 Breast Health Coach AI" по-долу. Образователни, не лечебни.',
    forceRefresh: "Нов AI анализ",
    noEnrichment: "Все още няма AI препоръки.",
    timeoutMsg: "AI отговорът отнема повече от обичайното. Опитайте отново след минута — отговорът се обработва и ще се появи кеширан.",
    noTrends:
      "Все още нямате snapshot-и за тенденции. Запишете първи snapshot, за да започне 3-месечното проследяване.",
    ariaCard: "Карта на биологична асоциация",
    screeningBannerTitle: "Внимание · Обичайните проверки остават задължителни",
    screeningBannerBody:
      "Ниската активационна карта на молекулярни субтипове (Луминален A/B, HER2+, TNBC) НЕ означава «безопасност» — тя означава само, че по текущите BHI сигнали моделът не открива силна асоциация. Клиничният риск изисква обичайните проверки: мамография от 40+ г. (или по-рано при фамилна анамнеза), MRI при висока плътност или BRCA1/2, генетичен тест при 1-ва степен BC/OVCA роднина.",
    screeningBannerCTA: "Вижте насоките за проверките",
  },
  en: {
    title: "🧬 Geno Lab — Biological Disease Intelligence",
    subtitle:
      "Educational profile of the biological associations between your BHI signals and {n} breast diseases. Surfaces ACTIVATED PATHWAYS and MODIFIABLE FACTORS — not a diagnosis, not a prediction, not a replacement for a physician.",
    legalNote:
      "Educational · pathway-based model · NOT a diagnostic or prognostic device. Always discuss with your physician.",
    refresh: "Refresh",
    enrich: "✨ AI recommendations (top 3)",
    enriching: "Generating AI recommendations…",
    saveSnapshot: "Save snapshot manually",
    snapshotSaved: "Snapshot saved",
    autoSnapshotHint: "🔄 Snapshots are saved automatically whenever lab or lifestyle data is updated (max 1 every 6 hours).",
    trends: "3-month trends",
    cached: "cached",
    loading: "Loading…",
    empty: "No data to display.",
    confidence: "Profile confidence",
    cohort: "Distribution across {n} cards",
    high: "High",
    elevated: "Elevated",
    moderate: "Moderate",
    low: "Low",
    section_drivers: "Biological drivers",
    section_pathways: "Activated signalling pathways",
    section_modifiable: "Modifiable factors",
    section_prevention: "Personalized prevention targets",
    section_contrib: "Top contributors to the profile",
    factor_label: "factor",
    weight: "weight",
    burden: "burden",
    score_label: "activation score",
    seeMore: "See full details",
    seeLess: "Hide",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    aiTitle: "🔬 AI Lab recommendations (DUTCH/DNA only)",
    aiHint:
      "Narrow-scope suggestions derived ONLY from the top-3 biological association cards above (DUTCH hormonal metabolites and DNA polymorphisms). For a holistic weekly plan see the ‘🤝 Breast Health Coach AI’ below. Educational, not therapeutic.",
    forceRefresh: "New AI analysis",
    noEnrichment: "No AI recommendations yet.",
    timeoutMsg: "AI is taking longer than usual. Try again in a minute — the result is being processed and will appear cached.",
    noTrends:
      "No trend snapshots yet. Save the first snapshot to begin 3-month tracking.",
    ariaCard: "Biological association card",
    screeningBannerTitle: "Notice · Standard screening remains required",
    screeningBannerBody:
      "A low activation card for molecular subtypes (Luminal A/B, HER2+, TNBC) does NOT mean \"safe\" — it only means the model does not detect a strong association from your current BHI signals. Clinical risk requires standard screening: mammography from age 40+ (earlier for family history), MRI for dense breasts or BRCA1/2, genetic testing for a 1st-degree BC/OVCA relative.",
    screeningBannerCTA: "See screening guidelines",
  },
  es: {
    title: "🧬 Geno Lab — Inteligencia Biológica de Enfermedades",
    subtitle:
      "Perfil educativo de las asociaciones biológicas entre sus señales BHI y {n} enfermedades mamarias. Muestra VÍAS ACTIVADAS y FACTORES MODIFICABLES — no es diagnóstico, no es predicción, no reemplaza al médico.",
    legalNote:
      "Educativo · modelo basado en vías · NO es un dispositivo diagnóstico o pronóstico. Consulte siempre a su médico.",
    refresh: "Actualizar",
    enrich: "✨ Recomendaciones IA (top 3)",
    enriching: "Generando recomendaciones IA…",
    saveSnapshot: "Guardar snapshot manualmente",
    snapshotSaved: "Snapshot guardado",
    autoSnapshotHint: "🔄 Los snapshots se guardan automáticamente al actualizar datos de laboratorio o estilo de vida (máx. 1 cada 6 horas).",
    trends: "Tendencias 3 meses",
    cached: "en caché",
    loading: "Cargando…",
    empty: "Sin datos para mostrar.",
    confidence: "Confianza del perfil",
    cohort: "Distribución entre las {n} tarjetas",
    high: "Alta",
    elevated: "Elevada",
    moderate: "Moderada",
    low: "Baja",
    section_drivers: "Conductores biológicos",
    section_pathways: "Vías de señalización activadas",
    section_modifiable: "Factores modificables",
    section_prevention: "Objetivos de prevención personalizados",
    section_contrib: "Top contribuyentes al perfil",
    factor_label: "factor",
    weight: "peso",
    burden: "carga",
    score_label: "puntuación de activación",
    seeMore: "Ver detalles completos",
    seeLess: "Ocultar",
    expandAll: "Expandir todo",
    collapseAll: "Contraer todo",
    aiTitle: "Recomendaciones IA personalizadas",
    aiHint:
      "Recomendaciones generadas por un modelo clínico-nutrigenómico sobre las 3 asociaciones más fuertes. Educativas, no terapéuticas.",
    forceRefresh: "Nuevo análisis IA",
    noEnrichment: "Aún no hay recomendaciones IA.",
    timeoutMsg: "La IA está tardando más de lo habitual. Vuelva a intentarlo en un minuto — el resultado se está procesando y aparecerá en caché.",
    noTrends:
      "Sin snapshots de tendencia. Guarde el primer snapshot para iniciar el seguimiento de 3 meses.",
    ariaCard: "Tarjeta de asociación biológica",
    screeningBannerTitle: "Aviso · El cribado estándar sigue siendo obligatorio",
    screeningBannerBody:
      "Una tarjeta de activación baja para los subtipos moleculares (Luminal A/B, HER2+, TNBC) NO significa \"seguridad\" — solo indica que el modelo no detecta una asociación fuerte con sus señales BHI actuales. El riesgo clínico requiere cribado estándar: mamografía desde los 40 años (antes con antecedentes familiares), MRI para mamas densas o BRCA1/2, prueba genética para familiar de 1er grado con BC/OVCA.",
    screeningBannerCTA: "Ver guías de cribado",
  },
};

const LEVEL_STYLES = {
  High:     { bg: "bg-red-100 dark:bg-red-500/20",       text: "text-red-700 dark:text-red-300",       dot: "bg-red-500" },
  Elevated: { bg: "bg-orange-100 dark:bg-orange-500/20", text: "text-orange-700 dark:text-orange-300", dot: "bg-orange-500" },
  Moderate: { bg: "bg-amber-100 dark:bg-amber-500/20",   text: "text-amber-700 dark:text-amber-300",   dot: "bg-amber-500" },
  Low:      { bg: "bg-emerald-100 dark:bg-emerald-500/20", text: "text-emerald-700 dark:text-emerald-300", dot: "bg-emerald-500" },
};

const FACTOR_LABELS = {
  bg: {
    estrogen: "естроген", insulin: "инсулин", igf1: "IGF-1", inflammation: "възпаление",
    ecm: "ECM/колаген", density: "плътност", adiposity: "адипозитет", vitamin_d: "витамин D",
    hereditary: "наследственост", dna_repair: "ДНК-репарация",
  },
  en: {
    estrogen: "estrogen", insulin: "insulin", igf1: "IGF-1", inflammation: "inflammation",
    ecm: "ECM/collagen", density: "density", adiposity: "adiposity", vitamin_d: "vitamin D",
    hereditary: "hereditary", dna_repair: "DNA repair",
  },
  es: {
    estrogen: "estrógeno", insulin: "insulina", igf1: "IGF-1", inflammation: "inflamación",
    ecm: "ECM/colágeno", density: "densidad", adiposity: "adiposidad", vitamin_d: "vitamina D",
    hereditary: "hereditario", dna_repair: "reparación ADN",
  },
};

/** Derive BDI input scores from the BHI snapshot returned by /breast-intelligence/snapshot. */
export function deriveBdiScoresFromSnapshot(snap) {
  if (!snap) return {};
  const comp = snap.metabolic?.components || {};
  const density = snap.density || {};
  const ecmPressure = density?.collagen_ecm?.ecm_pressure; // 0-100 (higher = MORE burden)
  const densityPct  = density?.density_percent;            // 0-100 (higher = MORE burden)
  const heredScore  = snap.signals?.family_history ? 0.6 : 0.0;
  return {
    estrogen_score:     comp.estrogen?.score,
    insulin_score:      comp.insulin?.score,
    igf1_score:         comp.igf1?.score,
    inflammation_score: comp.inflammation?.score,
    adiposity_score:    comp.adiposity?.score,
    ecm_score:          ecmPressure != null ? Math.max(0, 100 - ecmPressure) : undefined,
    density_score:      densityPct  != null ? Math.max(0, 100 - densityPct)  : undefined,
    vitamin_d_score:    undefined,
    hereditary_burden:  heredScore,
    dna_repair_burden:  heredScore,
  };
}

export default function BdiGenoLab({ snapshot, lang = "bg" }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const flabels = FACTOR_LABELS[lang] || FACTOR_LABELS.bg;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [expanded, setExpanded] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);

  const [enrichment, setEnrichment] = useState(null);
  const [enrichLoading, setEnrichLoading] = useState(false);

  const [snapshotMsg, setSnapshotMsg] = useState(null);
  const [trends, setTrends] = useState(null);

  const scores = useMemo(() => deriveBdiScoresFromSnapshot(snapshot), [snapshot]);

  const computeBdi = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.post(`${API}/bhi/disease-intelligence/preview?top_n=35`, scores);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [API, scores]);

  const fetchTrends = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bhi/disease-intelligence/trends?months=3`);
      setTrends(data);
    } catch {
      /* trends are optional */
    }
  }, [API]);

  useEffect(() => {
    if (snapshot) {
      computeBdi();
      fetchTrends();
    }
  }, [snapshot, computeBdi, fetchTrends]);

  // iter820 — Register cards into shared BdiHighlightProvider so the
  // ConditionChipsRow (parent BHI page) can build a reverse-index for
  // bidirectional hover highlighting. Also expose hover state to child
  // DiseaseCards below.
  // iter821 — Destructure `registerCards` (useCallback-stable) instead of
  // depending on the whole context object, otherwise every provider
  // re-render creates a new ctx reference → useEffect re-fires →
  // setState in registerCards → provider re-renders → INFINITE LOOP.
  const highlightCtx = useBdiHighlight();
  const { registerCards } = highlightCtx;
  useEffect(() => {
    if (data?.cards) registerCards(data.cards);
  }, [data?.cards, registerCards]);

  const runEnrichment = async (force = false) => {
    if (!data?.cards?.length) return;
    setEnrichLoading(true);
    try {
      // iter758 — Try async pattern first (avoids Cloudflare 524 for >100s calls)
      const { data: kickoff, status } = await axios.post(
        `${API}/bhi/disease-intelligence/enrich`,
        {
          lang,
          cards: data.cards.slice(0, 3),
          profile_normalised: data.profile_normalised,
          force_refresh: force,
          async: true,
        },
        { timeout: 30000, validateStatus: (s) => s < 500 },
      );

      // 200 → cache hit, done
      if (status === 200 && kickoff?.enrichment) {
        setEnrichment(kickoff);
        setEnrichLoading(false);
        return;
      }

      // 202 → job kicked off, poll status
      if (status === 202 && kickoff?.job_id) {
        const jobId = kickoff.job_id;
        const MAX_POLLS = 60;   // 60 * 3s = 180s max wait
        let polls = 0;
        while (polls < MAX_POLLS) {
          await new Promise(r => setTimeout(r, 3000));
          polls++;
          try {
            const { data: st } = await axios.get(
              `${API}/bhi/disease-intelligence/enrich/status/${jobId}`,
              { timeout: 15000 },
            );
            if (st.status === "done" && st.enrichment) {
              setEnrichment(st);
              setEnrichLoading(false);
              return;
            }
            if (st.status === "error") {
              setEnrichment({ error: st.error || "AI error", timed_out: false });
              setEnrichLoading(false);
              return;
            }
          } catch { /* transient network — keep polling */ }
        }
        setEnrichment({ error: t.timeoutMsg, timed_out: true });
      }
    } catch (e) {
      const status = e?.response?.status;
      const isTimeout = status === 502 || status === 504 || e?.code === "ECONNABORTED";
      const detail = e?.response?.data;
      const friendly = isTimeout || typeof detail !== "object";
      setEnrichment({
        error: friendly ? t.timeoutMsg : (detail?.detail || e.message),
        timed_out: isTimeout,
      });
    } finally {
      setEnrichLoading(false);
    }
  };

  const saveSnapshot = async () => {
    try {
      await axios.post(`${API}/bhi/disease-intelligence/snapshot`, { scores, top_n: 35 });
      setSnapshotMsg(t.snapshotSaved);
      fetchTrends();
      setTimeout(() => setSnapshotMsg(null), 2400);
    } catch (e) {
      setSnapshotMsg(e?.response?.data?.detail || e.message);
      setTimeout(() => setSnapshotMsg(null), 3600);
    }
  };

  const toggleCard = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  const toggleAll = () => {
    const next = !allExpanded;
    setAllExpanded(next);
    const map = {};
    (data?.cards || []).forEach((c) => { map[c.disease_id] = next; });
    setExpanded(map);
  };

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
  }`;

  const topDiseaseId = data?.cards?.[0]?.disease_id;
  const trendSeries = (topDiseaseId && trends?.by_disease?.[topDiseaseId]) || [];

  return (
    <div className="space-y-5" data-testid="bdi-geno-lab">
      {/* iter707d — GDPR + clinical utility disclosure (collapsible, top-most) */}
      <BdiPrivacyUtilityBanner lang={lang} isDark={isDark} />

      {/* Header */}
      <div className={`${cardCls} p-5`}>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center shrink-0">
            <Dna className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-xl font-extrabold bg-gradient-to-r from-fuchsia-500 to-purple-500 bg-clip-text text-transparent">
                {t.title}
              </h3>
              {data?.methodology && (
                <MethodologyInfo
                  methodology={data.methodology}
                  limitations={data.limitations}
                  lang={lang}
                  isDark={isDark}
                  testid="bdi-geno-lab"
                />
              )}
            </div>
            <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {(t.subtitle || "").replace("{n}", String(data?.total_diseases ?? 23))}
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={computeBdi}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300 hover:opacity-90 flex items-center gap-1.5"
            data-testid="bdi-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {t.refresh}
          </button>
          <button
            onClick={() => runEnrichment(false)}
            disabled={enrichLoading || !data?.cards?.length}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="bdi-enrich-btn"
          >
            {enrichLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {enrichLoading ? t.enriching : t.enrich}
          </button>
          <button
            onClick={saveSnapshot}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 hover:opacity-90 flex items-center gap-1.5"
            data-testid="bdi-snapshot-btn"
          >
            <Save className="w-3.5 h-3.5" /> {t.saveSnapshot}
          </button>
          {data?.cards?.length > 1 && (
            <button
              onClick={toggleAll}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 hover:opacity-90 flex items-center gap-1.5"
              data-testid="bdi-toggle-all-btn"
            >
              {allExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {allExpanded ? t.collapseAll : t.expandAll}
            </button>
          )}
          {snapshotMsg && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400" data-testid="bdi-snapshot-msg">
              {snapshotMsg}
            </span>
          )}
        </div>
      </div>

      {/* Legal disclaimer */}
      <div
        className={`p-3 rounded-xl border-l-4 flex items-start gap-2 ${
          isDark ? "bg-amber-500/10 border-amber-500" : "bg-amber-50 border-amber-500"
        }`}
        data-testid="bdi-disclaimer"
      >
        <ShieldAlert className={`w-4 h-4 shrink-0 mt-0.5 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
        <p className={`text-[11px] leading-relaxed ${isDark ? "text-amber-200" : "text-amber-700"}`}>
          {t.legalNote}
        </p>
      </div>

      {/* Cohort + confidence */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="bdi-cohort">
          <div className={`${cardCls} p-3`}>
            <div className="text-[10px] opacity-60">{t.confidence}</div>
            <div className="text-base font-bold">{data.confidence}</div>
          </div>
          {[
            ["High",     t.high,     "n_high"],
            ["Elevated", t.elevated, "n_elevated"],
            ["Moderate", t.moderate, "n_moderate"],
            ["Low",      t.low,      "n_low"],
          ].map(([lvl, label, key]) => {
            const s = LEVEL_STYLES[lvl];
            return (
              <div key={lvl} className={`${cardCls} p-3 flex items-center gap-2`}>
                <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                <div>
                  <div className="text-[10px] opacity-60">{label}</div>
                  <div className="text-base font-bold">{data.cohort_summary?.[key] ?? 0}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Loading / error */}
      {loading && (
        <div className="text-center py-10">
          <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-50" />
          <p className="mt-2 text-xs opacity-70">{t.loading}</p>
        </div>
      )}
      {err && (
        <div className="p-3 rounded-lg bg-red-500/10 text-red-500 text-sm" data-testid="bdi-error">
          {err}
        </div>
      )}

      {/* Disease cards */}
      {data?.cards?.length > 0 && (
        <>
          {/* iter753 — Screening reminder banner: fires when key molecular
              subtypes (Luminal A/B, HER2+, TNBC) all show Low activation.
              Low ≠ safe — it just means the model has no strong signal for
              those specific subtypes. Standard screening (mammography, MRI,
              BRCA testing) remains the clinical baseline. */}
          {(() => {
            const KEY_SUBTYPES = ["luminal_a", "luminal_b", "her2_positive", "tnbc"];
            const subtypesInCards = data.cards.filter(c => KEY_SUBTYPES.includes(c.disease_id));
            // Fire when AT LEAST ONE major molecular subtype shows Low
            // activation — Low ≠ safe for aggressive subtypes (HER2+, TNBC)
            // and standard screening remains required regardless of BDI.
            const anyLow = subtypesInCards.some(
              c => (c.association_level || "").toLowerCase() === "low"
            );
            if (!anyLow) return null;
            const lowIds = subtypesInCards
              .filter(c => (c.association_level || "").toLowerCase() === "low")
              .map(c => c.disease_id);
            return (
              <div
                data-testid="bdi-screening-reminder"
                className={`p-4 rounded-xl border-l-4 flex items-start gap-3 ${
                  isDark
                    ? "bg-indigo-500/10 border-indigo-400 text-indigo-100"
                    : "bg-indigo-50 border-indigo-500 text-indigo-900"
                }`}
              >
                <Stethoscope className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? "text-indigo-300" : "text-indigo-600"}`} />
                <div className="flex-1">
                  <div className={`text-sm font-semibold mb-1 ${isDark ? "text-indigo-200" : "text-indigo-800"}`}>
                    {t.screeningBannerTitle}
                  </div>
                  <p className="text-[12px] leading-relaxed opacity-90">
                    {t.screeningBannerBody}
                  </p>
                  {/* iter878 — methodology link removed (composite risk module deleted) */}
                </div>
              </div>
            );
          })()}

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="bdi-cards-grid">
          {data.cards.map((c, idx) => {
            // iter820 — Compute per-card highlight state from context.
            const affectedIds = highlightCtx.cardsByCondition[highlightCtx.hoveredCondition] || null;
            const isLit = !!(affectedIds && affectedIds.has(c.disease_id));
            const infCount = (c.condition_influences || []).length;
            const tooltipText = isLit
              ? `Влияе през ${(c.condition_influences || [])
                  .find((i) => i.condition_id === highlightCtx.hoveredCondition)?.matching_pathways?.length || 0} път${
                    ((c.condition_influences || []).find((i) => i.condition_id === highlightCtx.hoveredCondition)?.matching_pathways?.length || 0) === 1 ? "" : "а"
                  }`
              : (highlightCtx.hoveredDiseaseId === c.disease_id && infCount > 0
                  ? `${infCount} диагноз${infCount === 1 ? "а" : "и"} влия${infCount === 1 ? "е" : "ят"} на тази карта`
                  : "");
            return (
              <DiseaseCard
                key={c.disease_id}
                card={c}
                rank={idx + 1}
                expanded={!!expanded[c.disease_id]}
                onToggle={() => toggleCard(c.disease_id)}
                t={t}
                flabels={flabels}
                isDark={isDark}
                lang={lang}
                highlight={{
                  isLit,
                  tooltipText,
                  setHoveredDiseaseId: highlightCtx.setHoveredDiseaseId,
                }}
              />
            );
          })}
        </div>
        </>
      )}

      {/* AI enrichment block */}
      <div className={`${cardCls} p-5`} data-testid="bdi-ai-section">
        <div className="flex items-center gap-2 mb-2">
          <FlaskConical className="w-5 h-5 text-fuchsia-500" />
          <h4 className="text-base font-bold">{t.aiTitle}</h4>
          {enrichment?.cached && (
            <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              {t.cached}
            </span>
          )}
        </div>
        <p className={`text-[11px] mb-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t.aiHint}</p>

        {enrichment?.enrichment ? (
          <div
            className={`whitespace-pre-wrap text-sm leading-relaxed ${
              isDark ? "text-gray-200" : "text-gray-800"
            }`}
            data-testid="bdi-ai-text"
          >
            {enrichment.enrichment}
          </div>
        ) : enrichment?.error ? (
          <div className="text-sm text-red-500" data-testid="bdi-ai-error">{enrichment.error}</div>
        ) : (
          <div className="text-sm opacity-60 italic">{t.noEnrichment}</div>
        )}

        {enrichment?.enrichment && (
          <button
            onClick={() => runEnrichment(true)}
            disabled={enrichLoading}
            className="mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 hover:opacity-90 flex items-center gap-1.5"
            data-testid="bdi-ai-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${enrichLoading ? "animate-spin" : ""}`} /> {t.forceRefresh}
          </button>
        )}
      </div>

      {/* Trend chart */}
      <div className={`${cardCls} p-5`} data-testid="bdi-trends-section">
        <div className="flex items-center gap-2 mb-2">
          <History className="w-5 h-5 text-purple-500" />
          <h4 className="text-base font-bold">{t.trends}</h4>
        </div>
        <p className={`text-[11px] mb-3 ${isDark ? "text-gray-400" : "text-gray-500"}`} data-testid="bdi-auto-hint">
          {t.autoSnapshotHint}
        </p>
        {trendSeries.length >= 2 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trendSeries.map((s) => ({
              date: (s.created_at || "").slice(0, 10),
              score: s.score,
            }))}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="date" fontSize={10} />
              <YAxis domain={[0, 1]} tickFormatter={(v) => v.toFixed(1)} fontSize={10} />
              <Tooltip formatter={(v) => v?.toFixed?.(3) ?? v} />
              <Line type="monotone" dataKey="score" stroke="#A855F7" strokeWidth={2.2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-sm opacity-60 italic flex items-start gap-2">
            <BookOpen className="w-4 h-4 opacity-60 mt-0.5" />
            {t.noTrends}
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────── Card sub-component ─────────────────

function DiseaseCard({ card, rank, expanded, onToggle, t, flabels, isDark, lang = "bg", highlight }) {
  const s = LEVEL_STYLES[card.association_level] || LEVEL_STYLES.Low;
  const pct = Math.round((card.activation_score_0_1 || 0) * 100);
  const isLit = !!highlight?.isLit;
  const infCount = (card.condition_influences || []).length;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      onMouseEnter={() => highlight?.setHoveredDiseaseId?.(card.disease_id)}
      onMouseLeave={() => highlight?.setHoveredDiseaseId?.(null)}
      className={`rounded-2xl border overflow-hidden transition-all ${
        isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
      } ${isLit ? "ring-4 ring-amber-500 shadow-[0_0_0_2px_rgba(245,158,11,0.30)] -translate-y-0.5" : ""}`}
      aria-label={t.ariaCard}
      data-testid={`bdi-card-${card.disease_id}`}
      data-highlight={isLit ? "on" : "off"}
    >
      {isLit && highlight?.tooltipText && (
        <div className="px-3 py-1 bg-amber-500 text-white text-[10px] font-bold" data-testid={`bdi-card-highlight-tooltip-${card.disease_id}`}>
          {highlight.tooltipText}
        </div>
      )}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        data-testid={`bdi-card-toggle-${card.disease_id}`}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-bold opacity-50">#{rank}</span>
            <h5 className="font-bold text-sm leading-tight truncate">{card.disease}</h5>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {card.bhi_risk_factor_overlap?.shared_count > 0 && (
              <span
                className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500 text-white"
                title={`${card.bhi_risk_factor_overlap.driving_factors.length} от твоите избрани фактори активират този процес`}
                data-testid={`bdi-card-bhi-badge-${card.disease_id}`}
              >
                🎯 {card.bhi_risk_factor_overlap.driving_factors.length}
              </span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${s.bg} ${s.text}`}>
              {t[(card.association_level || "low").toLowerCase()] || card.association_level}
            </span>
          </div>
        </div>

        {/* Activation bar */}
        <div className="h-1.5 w-full rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
          <div
            className={`h-full ${s.dot.replace("bg-", "bg-")}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1.5 text-[10px] opacity-60">
          <span>{t.score_label}: {pct}%</span>
          <span className="flex items-center gap-0.5">
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {expanded ? t.seeLess : t.seeMore}
          </span>
        </div>

        {/* iter852 — Col3 / PRAL evidence chips (only when a modifier actually fired) */}
        <Col3PralChips modifier={card.col3_pral_modifier} isDark={isDark} lang={lang} diseaseId={card.disease_id} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 text-xs">
          {card.biological_drivers?.length > 0 && (
            <Detail label={t.section_drivers} items={card.biological_drivers} />
          )}
          {card.activated_pathways?.length > 0 && (
            <Detail label={t.section_pathways} items={card.activated_pathways} mono />
          )}
          {card.modifiable_factors?.length > 0 && (
            <Detail label={t.section_modifiable} items={card.modifiable_factors} accent="emerald" />
          )}
          {card.personalized_prevention_targets?.length > 0 && (
            <Detail
              label={t.section_prevention}
              items={card.personalized_prevention_targets}
              accent="pink"
            />
          )}
          {/* iter810 — Условия от анамнезата, които споделят метаболитни пътища
              с този disease. Помага на пациента да разбере ЗАЩО картата
              е активирана — не само чрез BHI scores, но и през директни
              диагнози (PCOS, IR, Hashimoto, BRCA, MTHFR…). */}
          {card.condition_influences?.length > 0 && (
            <div
              className={`rounded-lg p-2.5 border-l-4 ${
                isDark ? "bg-amber-500/10 border-amber-500/60" : "bg-amber-50 border-amber-400"
              }`}
              data-testid={`bdi-card-influences-${card.disease_id}`}
            >
              <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                Влияят от Вашата анамнеза
              </div>
              <ul className="space-y-1">
                {card.condition_influences.map((ci, i) => (
                  <li key={i} className="text-[11px]">
                    <b>{ci.condition_id}</b> — през{" "}
                    <span className={isDark ? "text-amber-200" : "text-amber-800"}>
                      {(ci.matching_pathways || []).map((p, j) => (
                        <span key={j}>
                          {j > 0 && <span className="opacity-50"> · </span>}
                          <span title={p.kegg || ""}>{p.name_bg || p.id}</span>
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* iter990 — BHI risk-factor overlap (unified from former BdiCrossLinkPanel).
              Shows which of the user's selected risk factors drive this disease's
              pathways, so there is a single canonical disease view. */}
          {card.bhi_risk_factor_overlap?.shared_count > 0 && (
            <div
              className={`rounded-lg p-2.5 border-l-4 ${
                isDark ? "bg-emerald-500/10 border-emerald-500/60" : "bg-emerald-50 border-emerald-400"
              }`}
              data-testid={`bdi-card-bhi-overlap-${card.disease_id}`}
            >
              <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                Активирано от твоите избрани рискови фактори
              </div>
              <div className="flex items-center gap-2 mb-1.5 text-[11px]">
                <span className={isDark ? "text-emerald-200" : "text-emerald-800"}>
                  <b>{card.bhi_risk_factor_overlap.shared_count}</b> общи процеса
                </span>
                <span className="opacity-50">·</span>
                <span
                  className={isDark ? "text-emerald-200" : "text-emerald-800"}
                  title="Активационно припокриване = сумарен BHI риск-скор на общите процеси"
                >
                  припокриване {(card.bhi_risk_factor_overlap.share_score).toFixed(2)}
                </span>
              </div>
              {/* iter992 — emoji pills of shared pathways so patient sees WHAT is shared, not just how many */}
              <div className="flex flex-wrap gap-1 mb-2">
                {card.bhi_risk_factor_overlap.shared_pathways.slice(0, 8).map((pid) => {
                  const meta = getPathwayMetaphor(pid);
                  return (
                    <span
                      key={pid}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] ${toneCls(meta.tone)}`}
                      title={meta.short}
                      data-testid={`bdi-pathway-pill-${pid}`}
                    >
                      <span>{meta.emoji}</span>
                      <span className="font-medium truncate max-w-[130px]">{pid.replace(/_/g, " ")}</span>
                    </span>
                  );
                })}
                {card.bhi_risk_factor_overlap.shared_pathways.length > 8 && (
                  <span className="text-[10px] opacity-60 self-center">
                    + още {card.bhi_risk_factor_overlap.shared_pathways.length - 8}
                  </span>
                )}
              </div>
              <ul className="space-y-1">
                {card.bhi_risk_factor_overlap.driving_factors.slice(0, 6).map((f) => (
                  <li key={f.factor_id} className="text-[11px] flex items-center gap-1.5">
                    <span
                      className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                        f.total_contribution < 0 ? "bg-teal-500" : "bg-emerald-500"
                      }`}
                    />
                    <span className={`shrink-0 font-medium ${
                      isDark ? "text-emerald-100" : "text-emerald-900"
                    }`}>
                      {f.modifiable ? "●" : "○"} {f.name_bg}
                    </span>
                    <span className="opacity-50 truncate">
                      през {f.pathways.length} {f.pathways.length === 1 ? "процес" : "процеса"}
                    </span>
                  </li>
                ))}
                {card.bhi_risk_factor_overlap.driving_factors.length > 6 && (
                  <li className="text-[10px] opacity-60">
                    + още {card.bhi_risk_factor_overlap.driving_factors.length - 6} фактора
                  </li>
                )}
              </ul>
            </div>
          )}

          {card.top_contributing_factors?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold opacity-50 mb-1">{t.section_contrib}</div>
              <ul className="space-y-0.5">
                {card.top_contributing_factors.map((tc, i) => (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <span className="capitalize">{flabels[tc.factor] || tc.factor}</span>
                    <span className="opacity-60">
                      {t.weight} {tc.weight} · {t.burden} {tc.burden_0_1}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* iter707d — Pathway Sensitivity Slider (client-side what-if simulator) */}
          {(card.all_contributing_factors?.length > 0 || card.top_contributing_factors?.length > 0) && (
            <PathwaySensitivitySlider card={card} lang={lang} isDark={isDark} />
          )}
          {/* iter849 — Discovery Card (educational AI panel) for rare subtypes only */}
          {isDiscoveryEligible(card.disease_id) && (
            <DiscoveryCardPanel
              diseaseId={card.disease_id}
              diseaseName={card.disease}
              lang={lang}
              isDark={isDark}
            />
          )}
          <p className="text-[10px] italic opacity-50 pt-1 border-t border-black/5 dark:border-white/5">
            {card.evidence_level}
          </p>
        </div>
      )}
    </motion.div>
  );
}

// iter839 — Layman-friendly Bulgarian translations of technical medical
// terms used in prevention_focus / modifiable_factors. When a term is
// present in this map, the Detail component shows the friendly label
// AND a tooltip with the original technical term.
const LAYMAN_BG = {
  // Phase I/II / detox
  "Фаза I детоксикация":     "Активиране на токсини в черния дроб (I-ва фаза)",
  "Фаза II детоксикация":    "Обезвреждане на токсини в черния дроб (II-ра фаза)",
  "Фаза II конюгация":       "Свързване на естроген с молекули за извеждане (II-ра фаза)",
  "Естрогенен клирънс":      "Очистване на естрогена от тялото",
  // KEGG pathways in plain language
  "PI3K-AKT сигнализация":   "Сигнален път на растеж (свързан с инсулин и IGF-1)",
  "mTOR регулация":          "Регулация на клетъчния растеж (mTOR ос)",
  "AMPK активация":          "Активиране на енергийния сензор AMPK",
  "IGF-1 сигнализация":      "Сигнал на растежния фактор IGF-1",
  "IL-6 / JAK-STAT баланс":  "Баланс на цитокина IL-6 (възпаление)",
  "TNF регулация":           "Регулация на цитокина TNF-α (възпаление)",
  "NF-κB регулация":         "Регулация на главния възпалителен ключ NF-κB",
  "ECM регулация":           "Регулация на извънклетъчния матрикс (структура на тъканта)",
  // Metabolism / hormonal
  "Инсулинова чувствителност": "Чувствителност към инсулин",
  "Гликемичен контрол":      "Контрол на кръвната захар",
  "Гликотоксичност":         "Токсичност от прекомерна захар (гликация)",
  "Оксидативен стрес":       "Оксидативен (окислителен) стрес",
  "Метаболитна гъвкавост":   "Метаболитна гъвкавост (превключване енергийни горива)",
  "Възпалителен товар":      "Ниво на общо възпаление в тялото",
  "Възпалителен статус":     "Възпалителен статус",
  "Имунна регулация":        "Регулация на имунната система",
  "Имунна модулация":        "Модулация на имунния отговор",
  "Пролиферативен контрол":  "Контрол на клетъчното размножаване",
  "Пролиферативна регулация":"Регулация на клетъчното размножаване",
  "Хормонален баланс":       "Хормонален баланс",
  "Стероиден баланс":        "Баланс на стероидните хормони",
  "Пролактинов баланс":      "Баланс на пролактина",
  "Андрогенен баланс":       "Баланс на мъжките хормони (андрогени)",
  "Гъстота на тъкан":        "Плътност на гръдната тъкан",
  "Стромална среда":         "Съединителна тъкан (строма) на гърдата",
  "Автофагия":               "Автофагия — клетъчно самопочистване",
};

function laymanize(term) {
  if (!term || typeof term !== "string") return term;
  const layman = LAYMAN_BG[term.trim()];
  return layman || term;
}

function Detail({ label, items, mono = false, accent = "slate" }) {
  const cls = {
    slate:   "bg-slate-100 dark:bg-slate-500/20 text-slate-700 dark:text-slate-300",
    emerald: "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
    pink:    "bg-pink-100 dark:bg-pink-500/20 text-pink-700 dark:text-pink-300",
  }[accent];
  return (
    <div>
      <div className="text-[10px] uppercase font-bold opacity-50 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((it, i) => {
          const raw = typeof it === "string" ? it : (it?.name || it?.label || String(it));
          const layman = laymanize(raw);
          const hasTranslation = layman !== raw;
          return (
            <span
              key={i}
              title={hasTranslation ? `Технически термин: ${raw}` : undefined}
              className={`px-2 py-0.5 rounded-full text-[10px] ${cls} ${mono ? "font-mono" : ""} ${hasTranslation ? "border-b border-dotted border-current cursor-help" : ""}`}
            >
              {layman}
            </span>
          );
        })}
      </div>
    </div>
  );
}



// ─── iter852 — Col3 / PRAL evidence chips ─────────────────────────────
// Small pills shown UNDER the activation bar on a disease card whenever the
// Col3 protective or PRAL aggravating modifier actually contributed. Absent
// otherwise (Truthful Patterns: no chip when the axis had no effect).
const _COL3_PRAL_TX = {
  bg: {
    col3_protect: (pct) => `🌱 Col3 защита -${pct}%`,
    col3_penalty: (pct) => `⚠️ Ниският Col3 добавя +${pct}%`,
    pral_penalty: (pct) => `🔥 PRAL киселинност +${pct}%`,
    pral_protect: (pct) => `🌿 Алкален PRAL -${pct}%`,
    tooltip_col3: "Col3 колагенов синтез повлиява ECM пътища на заболяването (2-седмична средна от log-натата храна).",
    tooltip_pral: "PRAL киселинният товар повлиява възпалителните пътища на заболяването.",
  },
  en: {
    col3_protect: (pct) => `🌱 Col3 protection -${pct}%`,
    col3_penalty: (pct) => `⚠️ Low Col3 adds +${pct}%`,
    pral_penalty: (pct) => `🔥 PRAL acid load +${pct}%`,
    pral_protect: (pct) => `🌿 Alkaline PRAL -${pct}%`,
    tooltip_col3: "Col3 collagen synthesis modulates the disease's ECM pathways (14-day meal-log average).",
    tooltip_pral: "PRAL acid load modulates the disease's inflammatory pathways.",
  },
  es: {
    col3_protect: (pct) => `🌱 Protección Col3 -${pct}%`,
    col3_penalty: (pct) => `⚠️ Col3 bajo suma +${pct}%`,
    pral_penalty: (pct) => `🔥 PRAL ácido +${pct}%`,
    pral_protect: (pct) => `🌿 PRAL alcalino -${pct}%`,
    tooltip_col3: "Col3 modula las vías ECM (media de 14 días).",
    tooltip_pral: "PRAL modula las vías inflamatorias.",
  },
};

function Col3PralChips({ modifier, isDark, lang = "bg", diseaseId }) {
  if (!modifier || typeof modifier !== "object") return null;
  const tx = _COL3_PRAL_TX[lang] || _COL3_PRAL_TX.bg;
  const col3_delta = Number(modifier.col3_delta || 0);
  const pral_delta = Number(modifier.pral_delta || 0);
  if (col3_delta === 0 && pral_delta === 0) return null;

  const chips = [];
  if (col3_delta < 0) {
    chips.push({
      key: "col3-protect",
      label: tx.col3_protect(Math.round(Math.abs(col3_delta) * 100)),
      tooltip: tx.tooltip_col3,
      cls: isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                  : "bg-emerald-100 text-emerald-800 border-emerald-300",
    });
  } else if (col3_delta > 0) {
    chips.push({
      key: "col3-penalty",
      label: tx.col3_penalty(Math.round(col3_delta * 100)),
      tooltip: tx.tooltip_col3,
      cls: isDark ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                  : "bg-amber-100 text-amber-800 border-amber-300",
    });
  }
  if (pral_delta > 0) {
    chips.push({
      key: "pral-penalty",
      label: tx.pral_penalty(Math.round(pral_delta * 100)),
      tooltip: tx.tooltip_pral,
      cls: isDark ? "bg-red-500/20 text-red-300 border-red-500/40"
                  : "bg-red-100 text-red-800 border-red-300",
    });
  } else if (pral_delta < 0) {
    chips.push({
      key: "pral-protect",
      label: tx.pral_protect(Math.round(Math.abs(pral_delta) * 100)),
      tooltip: tx.tooltip_pral,
      cls: isDark ? "bg-teal-500/20 text-teal-300 border-teal-500/40"
                  : "bg-teal-100 text-teal-800 border-teal-300",
    });
  }

  if (chips.length === 0) return null;

  return (
    <div
      className="flex flex-wrap gap-1 mt-1.5"
      data-testid={`bdi-col3-pral-chips-${diseaseId}`}
    >
      {chips.map((c) => (
        <span
          key={c.key}
          title={c.tooltip}
          data-testid={`bdi-chip-${c.key}-${diseaseId}`}
          className={`px-2 py-0.5 rounded-full text-[9px] font-semibold border ${c.cls} cursor-help`}
        >
          {c.label}
        </span>
      ))}
    </div>
  );
}
